import React, { useRef } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Paper,
  SvgIcon,
  Tooltip,
  Typography,
  makeStyles,
  MenuItem,
  Menu,
  colors, Button,
} from '@material-ui/core';
import {
  AttachFile,
  PlaylistAdd,
} from '@material-ui/icons';
import {
  X as XIcon,
  Maximize as MaximizeIcon,
  Minimize as MinimizeIcon,
  Clipboard,
} from 'react-feather';
import clsx from 'clsx';

import useGetRequest from 'src/hooks/useGetRequest';
import TextField from 'src/components/TextField';
import QuillEditor from 'src/components/QuillEditor';
import {
  previewEmailForTemplate,
  getContextForFields
} from 'src/utils/api';
import ChipAutocomplete from 'src/components/ChipAutocomplete';
import SlateRichTextArea from '../../../components/slate/SlateRichTextArea';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: `calc(100% - ${theme.spacing(6)}px)`,
    maxHeight: `calc(100% - ${theme.spacing(6)}px)`,
    width: 600,
    position: 'fixed',
    bottom: 0,
    right: 0,
    margin: theme.spacing(3),
    outline: 'none',
    zIndex: 1300,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 500
  },
  fullScreen: {
    height: '100%',
    width: '100%'
  },
  input: {
    width: '100%'
  },
  menu: {
    zIndex: 2001,
  },
  editor: {
    flexGrow: 1,
    '& .ql-editor': {
      minHeight: 300
    }
  },
  action: {
    marginRight: theme.spacing(1)
  },
  isTracked: {
    color: colors.blue['500'],
  }
}));

const fieldOptions = [
  {
    label: 'Client full name',
    value: 'client_full_name',
  },
  {
    label: 'Realtor full name',
    value: 'realtor_full_name',
  },
  {
    label: 'Outstanding tasks',
    value: 'outstanding_tasks',
  },
];

function EmailEditor({
  // only used for templating
  engagementId,

  // only used for variables
  workflowId,

  onClose,
  actions,
  title,
  recipients,
  setRecipients,
  ccs,
  setCcs,
  bccs,
  setBccs,
  subject,
  setSubject,
  body,
  setBody,
  isTracked,
  toggleIsTracked
}: any) {
  const classes = useStyles();
  const [fullScreen, setFullScreen] = React.useState(false);
  const [isFieldsMenuOpen, setFieldsMenuOpen] = React.useState(false);
  const [isTemplatesMenuOpen, setTemplatesMenuOpen] = React.useState(false);
  const quillRef = useRef(null);
  const [showCc, setShowCc] = React.useState(false);
  const [showBcc, setShowBcc] = React.useState(false);

  const { result: emailTemplates } = useGetRequest({
    url: '/automatic/email-templates/',
    initialResult: [],
  });

  const { result: roleAssignments } = useGetRequest({
    url: `/automatic/engagements/${engagementId}/role-assignments/`,
    initialResult: [],
  });

  const fieldsAnchorEl = React.useRef(null);
  const templatesAnchorEl = React.useRef(null);

  const handleExitFullScreen = () => setFullScreen(false);

  const handleEnterFullScreen = () => setFullScreen(true);

  const handleSelectField = (field: any) => {
    // get value for engagement id and field id
    const meow = quillRef;
    getContextForFields(engagementId, [field]).then((context) => {
      // @ts-ignore - Object is possibly 'null'.
      const editor = meow.current.getEditor();
      const range = editor.getSelection();

      editor.clipboard.dangerouslyPasteHTML(range ? range.index : 0, context[field]);
      setFieldsMenuOpen(false);
    });
    // getContextForFields(engagementId, [field]).then(context => {
    //   const editor = meow.current.getEditor();
    //   const { index } = editor.getSelection();
    //   const newBody = body.slice(0, index) + context[field] + body.slice(index);
    //
    //   setBody(newBody);
    //   setFieldsMenuOpen(false);
    // })
  };

  const handleSelectTemplate = (template: any) => () => {
    previewEmailForTemplate(engagementId, template.id).then((res) => {
      setRecipients(res.recipients);
      setSubject(res.subject);
      setBody(res.body);
      if (res.isTracked !== isTracked) toggleIsTracked();
      setTemplatesMenuOpen(false);
    });
  };

  return (
    <Paper
      className={clsx(
        classes.root,
        { [classes.fullScreen]: fullScreen }
      )}
      elevation={12}
    >
      <Box
        bgcolor="background.dark"
        display="flex"
        alignItems="center"
        py={1}
        px={2}
      >
        <Typography
          variant="h5"
          color="textPrimary"
        >
          {title}
        </Typography>
        <Box flexGrow={1} />
        {fullScreen ? (
          <IconButton onClick={handleExitFullScreen}>
            <SvgIcon fontSize="small">
              <MinimizeIcon />
            </SvgIcon>
          </IconButton>
        ) : (
          <IconButton onClick={handleEnterFullScreen}>
            <SvgIcon fontSize="small">
              <MaximizeIcon />
            </SvgIcon>
          </IconButton>
        )}
        <IconButton onClick={onClose}>
          <SvgIcon fontSize="small">
            <XIcon />
          </SvgIcon>
        </IconButton>
      </Box>
      <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" flexGrow="1">
          <Box pr={1}>
            <Typography variant="h5">
              To:
            </Typography>
          </Box>
          <ChipAutocomplete
            options={roleAssignments.map((ra: any) => ra.account)}
            getOptionLabel={(role: any) => role && `${role.firstName} ${role.lastName}`}
            value={recipients}
            onChange={(event: any, _recipients: any) => {
              setRecipients(_recipients);
            }}
            noBorder
          />
        </Box>
        <Box display="flex">
          <Button onClick={() => setShowCc(!showCc)}>
            Cc
          </Button>
          <Button onClick={() => setShowBcc(!showBcc)}>
            Bcc
          </Button>
        </Box>
      </Box>
      <Divider />
      {
        (showCc || ccs.length) ? (
          <>
            <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
              <Box pr={1}>
                <Typography variant="h5">
                  Cc:
                </Typography>
              </Box>
              <ChipAutocomplete
                options={roleAssignments.map((ra: any) => ra.account)}
                getOptionLabel={(role: any) => role && `${role.firstName} ${role.lastName}`}
                value={ccs}
                onChange={(event: any, _ccs: any) => {
                  setCcs(_ccs);
                }}
                noBorder
              />
            </Box>
            <Divider />
          </>
        ) : null
      }
      {
        (showBcc || bccs.length) ? (
          <>
            <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
              <Box pr={1}>
                <Typography variant="h5">
                  Bcc:
                </Typography>
              </Box>
              <ChipAutocomplete
                options={roleAssignments.map((ra: any) => ra.account)}
                getOptionLabel={(role: any) => role && `${role.firstName} ${role.lastName}`}
                value={bccs}
                onChange={(event: any, _bccs: any) => {
                  setBccs(_bccs);
                }}
                noBorder
              />
            </Box>
            <Divider />
          </>
        ) : null
      }
      <Box pl={2} pr={2} pt={1} pb={1} display="flex" alignItems="center">
        <Box pr={1}>
          <Typography variant="h5">
            Subject:
          </Typography>
        </Box>
        <TextField
          className={classes.input}
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="Subject"
          value={subject}
          onChange={(e: any) => setSubject(e.target.value)}
        />
      </Box>
      <Divider />
      <SlateRichTextArea
        value={body}
        workflowId={workflowId}
        onChange={(value: any) => setBody(value)}
      />
      <Divider />
      <Box
        display="flex"
        alignItems="center"
        py={1}
        px={2}
      >
        {actions}
        <Tooltip title="Attach file">
          <IconButton
            size="small"
            className={classes.action}
          >
            <AttachFile />
          </IconButton>
        </Tooltip>
        {
          engagementId && (
            <>
              <Tooltip title="Add field">
                <IconButton
                  size="small"
                  className={classes.action}
                  ref={fieldsAnchorEl}
                  onClick={() => setFieldsMenuOpen(true)}
                >
                  <PlaylistAdd />
                </IconButton>
              </Tooltip>
              <Menu
                className={classes.menu}
                anchorEl={fieldsAnchorEl.current}
                open={isFieldsMenuOpen}
                PaperProps={{ style: { width: 250 } }}
                onClose={() => setFieldsMenuOpen(false)}
              >
                {
                  fieldOptions.map(({ label, value }) => (
                    <MenuItem key={value} onClick={() => handleSelectField(value)}>
                      {label}
                    </MenuItem>
                  ))
                }
              </Menu>
            </>
          )
        }
        {
          engagementId && (
            <>
              <Tooltip title="Choose template">
                <IconButton
                  size="small"
                  className={classes.action}
                  ref={templatesAnchorEl}
                  onClick={() => setTemplatesMenuOpen(true)}
                >
                  <Clipboard />
                </IconButton>
              </Tooltip>
              <Menu
                className={classes.menu}
                anchorEl={templatesAnchorEl.current}
                open={isTemplatesMenuOpen}
                PaperProps={{ style: { width: 250 } }}
                onClose={() => setTemplatesMenuOpen(false)}
              >
                {
                  emailTemplates.map((template: any) => (
                    <MenuItem key={template.id} onClick={handleSelectTemplate(template)}>
                      {template.subject}
                    </MenuItem>
                  ))
                }
              </Menu>
            </>
          )
        }
      </Box>
    </Paper>
  );
}

export default EmailEditor;
